import React, { useEffect, useRef, useState } from "react";
import {
  FileCode,
  AlertTriangle,
  Activity,
  RefreshCw,
  Loader2,
  Network,
  ArrowRight,
  Beaker,
} from "lucide-react";
import Button from "../../../components/forms/Button";
import { qaApi, RISK_META, RECOMMENDATION_META, formatTime } from "./api";

const PIPELINE_STEPS = [
  { key: "received", label: "Received" },
  { key: "detecting_changes", label: "Change Detection" },
  { key: "building_graph", label: "Dependency Graph" },
  { key: "scoring_risk", label: "Risk Score" },
  { key: "running_agents", label: "AI Agents" },
  { key: "running_tests", label: "Tests" },
  { key: "generating_report", label: "Report" },
  { key: "completed", label: "Done" },
];

// Honest empty-state text per backend `status` — so we don't blame Neo4j when
// it's actually up and the file simply has no code relationships to graph.
const SUBGRAPH_EMPTY_MESSAGE = {
  "neo4j-disabled":
    "Dependency graph is disabled (Neo4j is not connected on the server).",
  "unsupported-file":
    "No dependency graph for this file type — only source files (.js/.ts/.jsx/.tsx/.dart) are graphed.",
  "no-relationships":
    "No recorded relationships for this file — it doesn't define an endpoint, call an API, or access a DB collection.",
  error: "Couldn't load the dependency graph — please try again.",
  default: "No graph data for this file.",
};

const CHANGE_COLOR = {
  added: "text-green-600 dark:text-green-400",
  modified: "text-yellow-600 dark:text-yellow-400",
  deleted: "text-red-600 dark:text-red-400",
  renamed: "text-blue-600 dark:text-blue-400",
};

const isTerminal = (status) => status === "completed" || status === "failed";

const LiveAnalysis = ({ commitId, onClear }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subgraph, setSubgraph] = useState(null);
  const [graphLoadingPath, setGraphLoadingPath] = useState(null);
  const pollRef = useRef(null);

  const fetchDetail = async (id) => {
    const d = await qaApi.getCommit(id);
    setData(d);
    return d;
  };

  useEffect(() => {
    if (!commitId) return;
    setLoading(true);
    fetchDetail(commitId)
      .then((d) => {
        // poll while the pipeline is still running
        if (!isTerminal(d?.commit?.status)) {
          pollRef.current = setInterval(async () => {
            const fresh = await fetchDetail(commitId);
            if (isTerminal(fresh?.commit?.status)) clearInterval(pollRef.current);
          }, 2500);
        }
      })
      .finally(() => setLoading(false));

    return () => clearInterval(pollRef.current);
  }, [commitId]);

  const loadSubgraph = async (filePath, repoId) => {
    setGraphLoadingPath(filePath);
    // Show the panel immediately in a loading state so the user gets feedback.
    setSubgraph({ filePath, loading: true });
    try {
      const g = await qaApi.getSubgraph(filePath, repoId);
      setSubgraph({ filePath, ...g });
    } catch {
      setSubgraph({ filePath, nodes: [], edges: [], status: "error" });
    } finally {
      setGraphLoadingPath(null);
    }
  };

  const handleReanalyze = async () => {
    await qaApi.reanalyzeCommit(commitId);
    fetchDetail(commitId);
  };

  if (!commitId) {
    return (
      <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-10 text-center">
        <Activity className="w-8 h-8 mx-auto text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 dark:text-light-500">
          Select a commit from the Overview to see its live analysis.
        </p>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analysis…
      </div>
    );
  }

  const { commit, changedFiles = [], signals = [], risk, report, repo } = data || {};
  const currentStepIndex = PIPELINE_STEPS.findIndex((s) => s.key === commit?.status);
  const impact = report?.impactSummary || {};
  const bugRisks = report?.bugRisks || [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-light-100 truncate">
              {commit?.message?.split("\n")[0] || commit?.sha?.slice(0, 7)}
            </p>
            <p className="text-xs text-gray-500 dark:text-light-500 mt-0.5">
              {repo ? `${repo.organization}/${repo.name} · ` : ""}
              {commit?.sha?.slice(0, 7)} · {commit?.author || "unknown"}
              {commit?.prNumber ? ` · PR #${commit.prNumber}` : ""} · {formatTime(commit?.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" icon={<RefreshCw className="w-4 h-4" />} onClick={handleReanalyze}>
              Re-run
            </Button>
            <Button variant="outline" size="sm" onClick={onClear}>
              Close
            </Button>
          </div>
        </div>

        {/* Pipeline progress */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {PIPELINE_STEPS.map((step, i) => {
            const done = commit?.status === "failed" ? i < currentStepIndex : i <= currentStepIndex;
            const active = i === currentStepIndex && !isTerminal(commit?.status);
            return (
              <React.Fragment key={step.key}>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                    done
                      ? "bg-primary-accent/15 text-primary-accent"
                      : "bg-gray-100 dark:bg-dark-400 text-gray-400"
                  }`}
                >
                  {active && <Loader2 className="w-3 h-3 animate-spin" />}
                  {step.label}
                </span>
                {i < PIPELINE_STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-gray-300" />}
              </React.Fragment>
            );
          })}
        </div>
        {commit?.status === "failed" && (
          <p className="mt-3 text-xs text-red-500">Pipeline failed: {commit.error}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Risk + recommendation */}
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-3">Risk Score</h3>
          {risk ? (
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 dark:text-light-100">{risk.score}</div>
              <span
                className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                  RISK_META[risk.level]?.className || ""
                }`}
              >
                {risk.level}
              </span>
              <p className="text-xs text-gray-500 dark:text-light-500 mt-2">
                base {risk.base} × {risk.multiplier} · {risk.affectedFrontendCount} frontend impacted
              </p>
              {report?.recommendation && (
                <span
                  className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold ${
                    RECOMMENDATION_META[report.recommendation]?.className || ""
                  }`}
                >
                  {RECOMMENDATION_META[report.recommendation]?.label}
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-light-500">Pending…</p>
          )}
        </div>

        {/* Impact summary */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-3">Impact Analysis</h3>
          {report ? (
            <div className="space-y-3 text-sm">
              {report.aiNarrative && (
                <p className="text-gray-700 dark:text-light-300 leading-relaxed">{report.aiNarrative}</p>
              )}
              <ImpactRow label="Affected frontend" items={impact.affected_frontend} />
              <ImpactRow label="Affected endpoints" items={impact.affected_endpoints} />
              <ImpactRow label="Affected modules" items={impact.affected_modules} />
              {report.testPlan?.suites?.length > 0 && (
                <ImpactRow label="Recommended tests" items={report.testPlan.suites} />
              )}
              {impact.confidence && (
                <p className="text-xs text-gray-500 dark:text-light-500">Confidence: {impact.confidence}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-light-500">Report not generated yet.</p>
          )}
        </div>
      </div>

      {/* Bug risks */}
      {bugRisks.length > 0 && (
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" /> Potential Risks ({bugRisks.length})
          </h3>
          <div className="space-y-2">
            {bugRisks.map((b, i) => (
              <div key={i} className="p-3 rounded-xl bg-gray-50 dark:bg-dark-400 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${RISK_META[b.severity]?.className || "bg-gray-200"}`}>
                    {b.severity}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-light-100">{b.type}</span>
                </div>
                <p className="text-gray-600 dark:text-light-400">{b.description}</p>
                {b.file && <p className="text-xs text-gray-400 mt-1 font-mono">{b.file}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executed tests */}
      {report?.testResults?.results?.length > 0 && (
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-3 flex items-center gap-2">
            <Beaker className="w-4 h-4 text-primary-accent" /> Executed Tests
          </h3>
          <div className="space-y-2">
            {report.testResults.results.map((t, i) => {
              const tone =
                t.status === "PASS"
                  ? "bg-green-500/15 text-green-600 dark:text-green-300"
                  : t.status === "FAIL" || t.status === "ERROR"
                    ? "bg-red-500/15 text-red-600 dark:text-red-300"
                    : "bg-gray-400/15 text-gray-500 dark:text-light-400";
              return (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-dark-400 text-sm">
                  <span className="font-medium capitalize text-gray-900 dark:text-light-100">{t.testType}</span>
                  <div className="flex items-center gap-2">
                    {t.breaking && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-500/15 text-red-600 dark:text-red-300">
                        BREAKING
                      </span>
                    )}
                    {t.details?.p95 && (
                      <span className="text-xs text-gray-500 dark:text-light-500">P95 {t.details.p95}</span>
                    )}
                    {t.details?.failedStep && (
                      <span className="text-xs text-gray-500 dark:text-light-500">{t.details.failedStep}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tone}`}>{t.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Changed files + signals */}
      <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-3 flex items-center gap-2">
          <FileCode className="w-4 h-4 text-primary-accent" /> Changed Files ({changedFiles.length})
        </h3>
        <div className="space-y-1">
          {changedFiles.map((f) => {
            const fileSignals = signals.filter((s) => s.filePath === f.filePath);
            return (
              <div key={f._id} className="flex items-center justify-between py-1.5 text-sm">
                <button
                  className="flex items-center gap-2 min-w-0 hover:text-primary-accent transition disabled:opacity-60"
                  onClick={() => loadSubgraph(f.filePath, repo?._id)}
                  disabled={graphLoadingPath === f.filePath}
                  title="View dependency subgraph"
                >
                  {graphLoadingPath === f.filePath ? (
                    <Loader2 className="w-3.5 h-3.5 text-primary-accent shrink-0 animate-spin" />
                  ) : (
                    <Network className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  )}
                  <span className={`font-mono text-xs truncate ${CHANGE_COLOR[f.changeType] || ""}`}>
                    {f.filePath}
                  </span>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  {fileSignals.map((s) => (
                    <span key={s._id} className="px-1.5 py-0.5 rounded bg-primary-accent/10 text-primary-accent text-[10px] font-medium">
                      {s.signalType}
                    </span>
                  ))}
                  <span className="text-xs text-green-600">+{f.additions}</span>
                  <span className="text-xs text-red-500">-{f.deletions}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dependency subgraph viewer */}
      {subgraph && (
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-3 flex items-center gap-2">
            <Network className="w-4 h-4 text-primary-accent" /> Dependency Graph
            <span className="text-xs font-normal text-gray-400 font-mono">{subgraph.filePath}</span>
          </h3>
          {subgraph.loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-500 dark:text-light-500">
              <Loader2 className="w-6 h-6 animate-spin text-primary-accent" />
              <p className="text-sm">Loading dependency graph…</p>
            </div>
          ) : subgraph.nodes?.length ? (
            <SubgraphView nodes={subgraph.nodes} edges={subgraph.edges} />
          ) : (
            <p className="text-sm text-gray-500 dark:text-light-500">
              {SUBGRAPH_EMPTY_MESSAGE[subgraph.status] || SUBGRAPH_EMPTY_MESSAGE.default}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ImpactRow = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-light-500 w-36 shrink-0">{label}:</span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-dark-400 text-xs text-gray-700 dark:text-light-300">
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};

const NODE_TYPE_COLOR = {
  File: "var(--accent)",
  Endpoint: "#3b82f6",
  Component: "#22c55e",
  Collection: "#a855f7",
  Repository: "var(--faint)",
};

/* Minimal SVG node/edge renderer (no extra deps). Hub-and-spoke: the clicked
   file sits in the center with its related nodes arranged around it. */
const SubgraphView = ({ nodes, edges }) => {
  const W = 720;
  const H = 460;
  const cx = W / 2;
  const cy = H / 2;
  // Every edge originates from the clicked file (the query is f-[r]-n), so the
  // common source is the hub. Fall back to the first node if edges are absent.
  const hubId = edges[0]?.source || nodes[0]?.id;
  const spokes = nodes.filter((n) => n.id !== hubId);
  // Keep the ring inside the canvas and scale labels down as nodes pile up.
  const radius = Math.min(cx, cy) - 90;

  const positioned = nodes.map((n) => {
    if (n.id === hubId) return { ...n, x: cx, y: cy, hub: true };
    const idx = spokes.findIndex((s) => s.id === n.id);
    const angle = (2 * Math.PI * idx) / Math.max(spokes.length, 1) - Math.PI / 2;
    return { ...n, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle), hub: false };
  });
  const byId = Object.fromEntries(positioned.map((n) => [n.id, n]));
  const presentTypes = Array.from(new Set(nodes.map((n) => n.type))).filter((t) => NODE_TYPE_COLOR[t]);

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[460px] max-h-[70vh]" preserveAspectRatio="xMidYMid meet">
        {edges.map((e, i) => {
          const s = byId[e.source];
          const t = byId[e.target];
          if (!s || !t) return null;
          return (
            <g key={i}>
              <line x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke="var(--line2)" strokeWidth="1.5" />
              <text
                x={(s.x + t.x) / 2}
                y={(s.y + t.y) / 2 - 4}
                fontSize="11"
                fill="var(--faint)"
                textAnchor="middle"
                stroke="white"
                strokeWidth="3"
                paintOrder="stroke"
                className="dark:[stroke:var(--panel-solid)]"
              >
                {e.type}
              </text>
            </g>
          );
        })}
        {positioned.map((n) => {
          const r = n.hub ? 16 : 12;
          const color = NODE_TYPE_COLOR[n.type] || "var(--faint)";
          return (
            <g key={n.id}>
              <circle cx={n.x} cy={n.y} r={r} fill={color} stroke="white" strokeWidth="2" className="dark:[stroke:var(--panel-solid)]" />
              <text
                x={n.x}
                y={n.y - r - 6}
                fontSize={n.hub ? 14 : 12}
                fontWeight={n.hub ? 700 : 500}
                fill="currentColor"
                textAnchor="middle"
                stroke="white"
                strokeWidth="3"
                paintOrder="stroke"
                className="text-gray-800 dark:text-light-200 dark:[stroke:var(--panel-solid)]"
              >
                {String(n.label).slice(0, 26)}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-dark-100">
        {presentTypes.map((t) => (
          <span key={t} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-light-400">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: NODE_TYPE_COLOR[t] }} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

export default LiveAnalysis;
